import { useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { ShieldCheck, ShieldAlert, Check, Circle } from 'lucide-react';
import { useCompanyVerification } from '@/hooks/useCompanyVerification';
import { DOC_TYPE_LABELS } from '@/lib/verification/docTypeLabels';
import { MultiFileUploadZone } from './MultiFileUploadZone';
import { UploadedDocsList } from './UploadedDocsList';

interface Props {
  companyId: string;
  role: string;
  isAdmin: boolean;
  documentsVerifiedAt: string | null | undefined;
}

export function CompanyVerificationTab({ companyId, role, isAdmin, documentsVerifiedAt }: Props) {
  const {
    loading,
    documents,
    requiredDocs,
    optionalDocs,
    latestByType,
    isVerified,
    refresh,
  } = useCompanyVerification(companyId, role);

  // Light polling while there are pending-parse rows so users see progress.
  useEffect(() => {
    const hasPending = documents.some((d) => d.review_status === 'pending_parse');
    if (!hasPending) return;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [documents, refresh]);

  if (loading) {
    return <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>;
  }

  const verified = isVerified || !!documentsVerifiedAt;

  return (
    <div className="space-y-4 mt-4">
      {/* Status header */}
      <Card className="p-4">
        <div className="flex items-center gap-3">
          {verified ? (
            <div className="h-10 w-10 rounded-full bg-success-subtle flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-success" />
            </div>
          ) : (
            <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
              <ShieldAlert className="h-5 w-5 text-amber-700" />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-bold text-foreground">
              {verified ? 'Documents Verified' : 'Verification Pending'}
            </div>
            <div className="text-xs text-muted-foreground">
              {documentsVerifiedAt
                ? `Verified ${new Date(documentsVerifiedAt).toLocaleDateString()}`
                : 'Upload your compliance documents below — we’ll identify each one automatically.'}
            </div>
          </div>
        </div>
      </Card>

      {/* Unified multi-file drop zone (admin-only upload) */}
      {isAdmin && (
        <MultiFileUploadZone companyId={companyId} onUploaded={refresh} />
      )}

      {/* Required + optional checklist */}
      <Card className="p-4">
        <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
          For verification
        </h3>
        <div className="space-y-1.5">
          {requiredDocs.map((t) => {
            const d = latestByType(t);
            const ok = !!d && (d.review_status === 'approved' || d.review_status === 'auto_approved');
            return (
              <div key={t} className="flex items-center gap-2 text-sm">
                {ok ? (
                  <Check className="h-4 w-4 text-success" />
                ) : (
                  <Circle className="h-4 w-4 text-muted-foreground" />
                )}
                <span className={ok ? 'text-foreground' : 'text-muted-foreground'}>
                  {DOC_TYPE_LABELS[t]}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-1">Required</span>
              </div>
            );
          })}
          {optionalDocs.map((t) => {
            const d = latestByType(t);
            const ok = !!d && (d.review_status === 'approved' || d.review_status === 'auto_approved');
            return (
              <div key={t} className="flex items-center gap-2 text-sm">
                {ok ? (
                  <Check className="h-4 w-4 text-success" />
                ) : (
                  <Circle className="h-4 w-4 text-muted-foreground/50" />
                )}
                <span className={ok ? 'text-foreground' : 'text-muted-foreground'}>
                  {DOC_TYPE_LABELS[t]}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 ml-1">Optional</span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Uploaded files list */}
      <UploadedDocsList documents={documents} />
    </div>
  );
}
